import type { Components } from '@/api/generated/api';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, type ComputedRef, type MaybeRef, toValue } from 'vue';
import {
  createUserFeedback as apiCreateUserFeedback,
  fetchUserFeedbackById,
  updateUserFeedback as apiUpdateUserFeedback,
} from '@/api/resources/userFeedback.api';
import {
  deleteUserFeedback as apiDeleteUserFeedback,
  fetchUserFeedback,
  setUserFeedbackHandled as apiSetUserFeedbackHandled,
} from '@/api/resources/userFeedback.provider.api';
import type { IFilter } from '@chax-at/prisma-filter-common';
import type { IUserFeedbackFilter } from 'common/interface/filter.interface';
import { userFeedbackKeys } from './userFeedback.keys';
import { usePermissions } from '../usePermissions';

interface IUserFeedbackComposable {
  allUserFeedback: ComputedRef<Components.Schemas.UserFeedbackDto[]>;
  totalCount: ComputedRef<number>;
  refresh: () => Promise<void>;
  createUserFeedback: (
    dto: Components.Schemas.CreateUserFeedbackDto,
    toastMessage?: string,
  ) => Promise<Components.Schemas.UserFeedbackDto>;
  updateUserFeedback: (
    id: number,
    dto: Components.Schemas.UpdateUserFeedbackDto,
    toastMessage?: string,
  ) => Promise<Components.Schemas.UserFeedbackDto>;
  deleteUserFeedback: (id: number) => Promise<void>;
  setUserFeedbackHandled: (id: number, handled: boolean) => Promise<void>;
  getUserFeedbackById: (id: number) => Promise<Components.Schemas.DetailedUserFeedbackDto>;
  getUserFeedback: (filter?: IFilter<IUserFeedbackFilter>) => Promise<{
    count: number;
    rows: Components.Schemas.UserFeedbackDto[];
  }>;
}

export const useUserFeedback = (params?: {
  filter?: MaybeRef<IFilter<IUserFeedbackFilter> | undefined>;
  search?: MaybeRef<string>;
}): IUserFeedbackComposable => {
  const { customSuccessToast, deleteSuccessToast, editSuccessToast, catchError } = useToast();
  const queryClient = useQueryClient();
  const { hasProviderAccess } = usePermissions();
  const filter = computed(() => toValue(params?.filter));
  const search = computed(() => toValue(params?.search));
  const listKey = computed(() => userFeedbackKeys.list(filter.value, search.value));
  const allUserFeedback = computed(() => listQuery.data.value?.rows ?? []);
  const totalCount = computed(() => listQuery.data.value?.count ?? 0);

  const getAllUserFeedback = async (): Promise<{ count: number; rows: Components.Schemas.UserFeedbackDto[] }> => {
    if (!hasProviderAccess.value) {
      return Promise.resolve({ count: 0, rows: [] });
    }

    return fetchUserFeedback({
      search: search.value,
      filter: {
        ...filter.value,
        order: filter.value?.order ?? [{ field: 'createdAt', dir: 'desc' }],
      },
    });
  };

  const listQuery = useQueryWithToast({
    queryKey: listKey,
    queryFn: getAllUserFeedback,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: userFeedbackKeys.lists() });
  };

  const createUserFeedback = async (
    dto: Components.Schemas.CreateUserFeedbackDto,
    toastMessage?: string,
  ): Promise<Components.Schemas.UserFeedbackDto> => {
    return catchError(async () => {
      const result = await apiCreateUserFeedback(dto);
      await refresh();
      if (toastMessage) customSuccessToast(toastMessage);
      return result;
    });
  };

  const updateUserFeedback = async (
    id: number,
    dto: Components.Schemas.UpdateUserFeedbackDto,
    toastMessage?: string,
  ): Promise<Components.Schemas.UserFeedbackDto> => {
    return catchError(async () => {
      const result = await apiUpdateUserFeedback({ id, dto });
      await refresh();
      if (toastMessage) customSuccessToast(toastMessage);
      return result;
    });
  };

  const deleteUserFeedback = async (id: number): Promise<void> => {
    await catchError(async () => {
      await apiDeleteUserFeedback({ id });
      await refresh();
      deleteSuccessToast();
    });
  };

  const setUserFeedbackHandled = async (id: number, handled: boolean): Promise<void> => {
    await catchError(async () => {
      await apiSetUserFeedbackHandled({ id, handled });
      await refresh();
      editSuccessToast();
    });
  };

  const getUserFeedbackById = (id: number): Promise<Components.Schemas.DetailedUserFeedbackDto> =>
    fetchUserFeedbackById({ id });

  const getUserFeedback = (filterParam?: IFilter<IUserFeedbackFilter>) => {
    if (!hasProviderAccess.value) {
      return Promise.resolve({ count: 0, rows: [] });
    }

    return fetchUserFeedback({ filter: filterParam });
  };

  return {
    allUserFeedback,
    totalCount,
    refresh,
    createUserFeedback,
    updateUserFeedback,
    deleteUserFeedback,
    setUserFeedbackHandled,
    getUserFeedbackById,
    getUserFeedback,
  };
};
